import { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import IosShareIcon from "@mui/icons-material/IosShare";
import useAdminRanking from "./hooks/useAdminRanking";
import { EmptyState } from "@/core/ui";
import StoryExportModal from "./components/StoryExportModal";

const TYPE_LABELS = { MASC: "Masculino", FEM: "Feminino", MISTA: "Mista" };


const Ranking = () => {
  const {
    rankings,
    categories,
    selectedRanking,
    selectedCategory,
    standings,
    loadingRankings,
    loadingStandings,
    setSelectedRanking,
    setSelectedCategory,
  } = useAdminRanking();

  const [storyModalOpen, setStoryModalOpen] = useState(false);

  const selectedRankingName = rankings.find((r) => r.id === selectedRanking)?.name ?? "";

  const MEDAL_ROW = {
    1: "rgba(255,213,79,0.08)",
    2: "rgba(189,189,189,0.07)",
    3: "rgba(191,120,71,0.08)",
  };

  if (!loadingRankings && rankings.length === 0) {
    return (
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
            Ranking
          </Typography>
        </Box>
        <EmptyState
          icon={<EmojiEventsOutlinedIcon sx={{ fontSize: 34 }} />}
          title="Nenhum ranking criado ainda"
          description="Quando um ranking for criada, ele aparecerá aqui com a classificação de todos os jogadores."
          sx={{ py: 8 }}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={{ xs: 1.5, sm: 0 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
            Classificação
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            Selecione o Ranking e categoria para visualizar a classificação atual.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<IosShareIcon />}
          onClick={() => setStoryModalOpen(true)}
          disabled={standings.length === 0}
          sx={{
            mt: { xs: 0, sm: 0.5 },
            whiteSpace: "nowrap",
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Exportar para Stories</Box>
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>Exportar</Box>
        </Button>
      </Stack>

      <Grid container rowSpacing={1.5} columnSpacing={{ xs: 0, md: 1.5 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="season-select-label">Ranking</InputLabel>
            <Select
              labelId="season-select-label"
              value={selectedRanking}
              label="Temporada"
              onChange={(event) => setSelectedRanking(event.target.value)}
            >
              {rankings.length === 0 ? (
                <MenuItem value="" disabled>
                  Nenhum ranking disponível
                </MenuItem>
              ) : (
                rankings.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="category-select-label">Categoria</InputLabel>
            <Select
              labelId="category-select-label"
              value={selectedCategory}
              label="Categoria"
              onChange={(event) => setSelectedCategory(event.target.value)}
            >
              {categories.length === 0 ? (
                <MenuItem value="" disabled>
                  Nenhuma categoria disponível
                </MenuItem>
              ) : (
                categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name} - {TYPE_LABELS[cat.type] ?? cat.type}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {loadingStandings ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={22} />
          <Typography variant="body2">Carregando ranking...</Typography>
        </Stack>
      ) : standings.length === 0 ? (
        <Alert severity="info">
          Nenhum resultado encontrado para esse filtro.
        </Alert>
      ) : (
        <>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 800, color: "text.secondary", fontSize: "0.72rem", letterSpacing: 0.4, py: 1.2 } }}>
                  <TableCell sx={{ pl: 2, width: 42 }}>#</TableCell>
                  <TableCell>Jogador</TableCell>
                  <TableCell align="center" title="Partidas jogadas">PJ</TableCell>
                  <TableCell align="center" title="Vitórias">V</TableCell>
                  <TableCell align="center" title="Derrotas">D</TableCell>
                  <TableCell align="center" title="Saldo de games">Saldo</TableCell>
                  <TableCell align="center" sx={{ pr: 2 }}>Pts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {standings.map((player) => {
                  const rowBg = MEDAL_ROW[player.position];
                  return (
                    <TableRow
                      key={player.id}
                      sx={{
                        background: rowBg ?? "transparent",
                        "&:last-child td": { border: 0 },
                        "& td": { py: 1 },
                      }}
                    >
                      <TableCell sx={{ pl: 2 }}>
                        <Typography sx={{ fontWeight: 800, color: "text.secondary", fontSize: "0.85rem" }}>
                          {player.position}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                          <Avatar
                            src={player.avatarUrl || undefined}
                            sx={{ width: 34, height: 34, bgcolor: player.position <= 3 ? "primary.main" : "action.selected", flexShrink: 0 }}
                          >
                            {player.name[0]}
                          </Avatar>
                          <Typography noWrap sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                            {player.name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{player.matchesPlayed}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "success.main" }}>{player.wins}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "error.main" }}>{player.losses}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            color: player.saldo > 0 ? "success.main" : player.saldo < 0 ? "error.main" : "text.secondary",
                          }}
                        >
                          {player.saldo > 0 ? `+${player.saldo}` : player.saldo}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ pr: 2 }}>
                        <Typography sx={{ fontWeight: 900, fontSize: "0.9rem" }}>{player.points}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <StoryExportModal
        open={storyModalOpen}
        onClose={() => setStoryModalOpen(false)}
        standings={standings}
        category={selectedCategory}
        categories={categories}
        rankingName={selectedRankingName}
      />
    </Stack>
  );
};

export default Ranking;
